// Built-in node types for the parametric system. Pure functions over typed
// inputs/outputs — see paramGraph.ts for the engine.
//
// Node categories:
//   value      — Number, Vector, Boolean (constants and parameter knobs)
//   primitive  — Box, Cylinder, Sphere, Vase, Profile (mesh sources)
//   transform  — Translate, Rotate, Scale (operate on a mesh, output mesh)
//   combine    — Merge (concatenate two meshes' triangles)
//   array      — LinearArray (repeat a mesh along a vector)
//   output     — Output (terminal node — its `in` port becomes app.triangles)

import type { NodeTypeDef, Vec3T } from './paramGraph';
import type { Triangle, Vec3 } from './types';
import { buildVaseMesh } from './vaseModel';
import { csgUnion, csgSubtract, csgIntersect } from './csg';

// ---------- Mesh primitives ----------

function makeTri(a: Vec3, b: Vec3, c: Vec3): Triangle {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;
  const l = Math.hypot(nx, ny, nz) || 1;
  return { a, b, c, normal: [nx / l, ny / l, nz / l] };
}

function quad(a: Vec3, b: Vec3, c: Vec3, d: Vec3): Triangle[] {
  return [makeTri(a, b, c), makeTri(a, c, d)];
}

function buildBox(sx: number, sy: number, sz: number): Triangle[] {
  const x = sx / 2, y = sy / 2, z = sz / 2;
  const v: Vec3[] = [
    [-x, -y, -z], [ x, -y, -z], [ x,  y, -z], [-x,  y, -z],
    [-x, -y,  z], [ x, -y,  z], [ x,  y,  z], [-x,  y,  z]
  ];
  return [
    ...quad(v[0], v[3], v[2], v[1]), // -Z
    ...quad(v[4], v[5], v[6], v[7]), // +Z
    ...quad(v[0], v[1], v[5], v[4]), // -Y
    ...quad(v[2], v[3], v[7], v[6]), // +Y
    ...quad(v[1], v[2], v[6], v[5]), // +X
    ...quad(v[0], v[4], v[7], v[3])  // -X
  ];
}

function buildCylinder(radius: number, height: number, segments: number): Triangle[] {
  const seg = Math.max(6, Math.floor(segments));
  const tris: Triangle[] = [];
  const top: Vec3[] = [];
  const bot: Vec3[] = [];
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    bot.push([Math.cos(a) * radius, Math.sin(a) * radius, 0]);
    top.push([Math.cos(a) * radius, Math.sin(a) * radius, height]);
  }
  // Side
  for (let i = 0; i < seg; i++) {
    const j = (i + 1) % seg;
    tris.push(...quad(bot[i], bot[j], top[j], top[i]));
  }
  // Caps (fan)
  const cb: Vec3 = [0, 0, 0];
  const ct: Vec3 = [0, 0, height];
  for (let i = 0; i < seg; i++) {
    const j = (i + 1) % seg;
    tris.push(makeTri(cb, bot[j], bot[i]));
    tris.push(makeTri(ct, top[i], top[j]));
  }
  return tris;
}

function buildSphere(radius: number, segments: number, rings: number): Triangle[] {
  const seg = Math.max(6, Math.floor(segments));
  const rng = Math.max(4, Math.floor(rings));
  const tris: Triangle[] = [];
  const grid: Vec3[][] = [];
  for (let i = 0; i <= rng; i++) {
    const v = i / rng;
    const phi = v * Math.PI - Math.PI / 2; // -π/2..π/2
    const cp = Math.cos(phi);
    const sp = Math.sin(phi);
    const ring: Vec3[] = [];
    for (let j = 0; j < seg; j++) {
      const u = j / seg;
      const theta = u * Math.PI * 2;
      ring.push([Math.cos(theta) * cp * radius, Math.sin(theta) * cp * radius, sp * radius]);
    }
    grid.push(ring);
  }
  for (let i = 0; i < rng; i++) {
    for (let j = 0; j < seg; j++) {
      const j2 = (j + 1) % seg;
      const a = grid[i][j], b = grid[i][j2], c = grid[i + 1][j2], d = grid[i + 1][j];
      tris.push(makeTri(a, b, c));
      tris.push(makeTri(a, c, d));
    }
  }
  return tris;
}

// ---------- Transforms ----------

function transformMesh(tris: Triangle[], fn: (p: Vec3) => Vec3): Triangle[] {
  return tris.map((t) => makeTri(fn(t.a), fn(t.b), fn(t.c)));
}

// ---------- Node definitions ----------

const NUMBER: NodeTypeDef = {
  type: 'value.number',
  category: 'Değer',
  label: 'Sayı',
  inputs: [],
  outputs: [{ id: 'out', name: 'Out', type: 'number' }],
  params: [{ id: 'value', name: 'Değer', type: 'number', defaultValue: 0 }],
  compute: (_inputs, params) => ({ out: Number(params.value ?? 0) })
};

const VECTOR: NodeTypeDef = {
  type: 'value.vec3',
  category: 'Değer',
  label: 'Vektör',
  inputs: [
    { id: 'x', name: 'X', type: 'number', defaultValue: 0 },
    { id: 'y', name: 'Y', type: 'number', defaultValue: 0 },
    { id: 'z', name: 'Z', type: 'number', defaultValue: 0 }
  ],
  outputs: [{ id: 'out', name: 'Out', type: 'vec3' }],
  compute: (i) => ({ out: [Number(i.x), Number(i.y), Number(i.z)] as Vec3T })
};

const BOX: NodeTypeDef = {
  type: 'primitive.box',
  category: 'Şekil',
  label: 'Kutu',
  inputs: [
    { id: 'sx', name: 'X boyu', type: 'number', defaultValue: 30 },
    { id: 'sy', name: 'Y boyu', type: 'number', defaultValue: 30 },
    { id: 'sz', name: 'Z boyu', type: 'number', defaultValue: 30 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildBox(Number(i.sx), Number(i.sy), Number(i.sz)) })
};

const CYLINDER: NodeTypeDef = {
  type: 'primitive.cylinder',
  category: 'Şekil',
  label: 'Silindir',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 15 },
    { id: 'h', name: 'Yükseklik', type: 'number', defaultValue: 40 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildCylinder(Number(i.r), Number(i.h), Number(i.segments)) })
};

const SPHERE: NodeTypeDef = {
  type: 'primitive.sphere',
  category: 'Şekil',
  label: 'Küre',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 18 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 24 },
    { id: 'rings', name: 'Halka', type: 'number', defaultValue: 16 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildSphere(Number(i.r), Number(i.segments), Number(i.rings)) })
};

const VASE_NODE: NodeTypeDef = {
  type: 'primitive.vase',
  category: 'Şekil',
  label: 'Vazo',
  inputs: [
    { id: 'height', name: 'Yükseklik', type: 'number', defaultValue: 100 },
    { id: 'bottomR', name: 'Alt R', type: 'number', defaultValue: 30 },
    { id: 'bellyR', name: 'Göbek R', type: 'number', defaultValue: 45 },
    { id: 'topR', name: 'Üst R', type: 'number', defaultValue: 28 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 64 },
    { id: 'rings', name: 'Halka', type: 'number', defaultValue: 48 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildVaseMesh({
      height: Number(i.height),
      bottomRadius: Number(i.bottomR),
      bellyRadius: Number(i.bellyR),
      topRadius: Number(i.topR),
      bellyHeight: 0.5,
      segments: Number(i.segments),
      rings: Number(i.rings),
      twist: 0,
      noiseAmplitude: 0,
      noiseFrequency: 6,
      wallThickness: 0,
      bottomThickness: 2
    })
  })
};

const TRANSLATE: NodeTypeDef = {
  type: 'transform.translate',
  category: 'Dönüşüm',
  label: 'Öteleme',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'x', name: 'X', type: 'number', defaultValue: 0 },
    { id: 'y', name: 'Y', type: 'number', defaultValue: 0 },
    { id: 'z', name: 'Z', type: 'number', defaultValue: 0 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const dx = Number(i.x), dy = Number(i.y), dz = Number(i.z);
    return { mesh: transformMesh(m, (p) => [p[0] + dx, p[1] + dy, p[2] + dz]) };
  }
};

const ROTATE: NodeTypeDef = {
  type: 'transform.rotate',
  category: 'Dönüşüm',
  label: 'Döndürme',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'axis', name: 'Eksen', type: 'number', defaultValue: 2 }, // 0=X 1=Y 2=Z
    { id: 'angle', name: 'Açı (°)', type: 'number', defaultValue: 0 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const ax = Math.round(Number(i.axis)) % 3;
    const a = (Number(i.angle) * Math.PI) / 180;
    const c = Math.cos(a), s = Math.sin(a);
    const fn = (p: Vec3): Vec3 => {
      if (ax === 0) return [p[0], c * p[1] - s * p[2], s * p[1] + c * p[2]];
      if (ax === 1) return [c * p[0] + s * p[2], p[1], -s * p[0] + c * p[2]];
      return [c * p[0] - s * p[1], s * p[0] + c * p[1], p[2]];
    };
    return { mesh: transformMesh(m, fn) };
  }
};

const SCALE: NodeTypeDef = {
  type: 'transform.scale',
  category: 'Dönüşüm',
  label: 'Ölçek',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'sx', name: 'X', type: 'number', defaultValue: 1 },
    { id: 'sy', name: 'Y', type: 'number', defaultValue: 1 },
    { id: 'sz', name: 'Z', type: 'number', defaultValue: 1 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const sx = Number(i.sx), sy = Number(i.sy), sz = Number(i.sz);
    return { mesh: transformMesh(m, (p) => [p[0] * sx, p[1] * sy, p[2] * sz]) };
  }
};

const MERGE: NodeTypeDef = {
  type: 'combine.merge',
  category: 'Birleştir',
  label: 'Birleştir',
  inputs: [
    { id: 'a', name: 'A', type: 'mesh' },
    { id: 'b', name: 'B', type: 'mesh' }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const a = (i.a as Triangle[]) ?? [];
    const b = (i.b as Triangle[]) ?? [];
    return { mesh: [...a, ...b] };
  }
};

const ARRAY: NodeTypeDef = {
  type: 'array.linear',
  category: 'Dizi',
  label: 'Lineer Dizi',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'count', name: 'Sayı', type: 'number', defaultValue: 3 },
    { id: 'dx', name: 'ΔX', type: 'number', defaultValue: 40 },
    { id: 'dy', name: 'ΔY', type: 'number', defaultValue: 0 },
    { id: 'dz', name: 'ΔZ', type: 'number', defaultValue: 0 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const n = Math.max(1, Math.floor(Number(i.count)));
    const dx = Number(i.dx), dy = Number(i.dy), dz = Number(i.dz);
    const out: Triangle[] = [];
    for (let k = 0; k < n; k++) {
      const ox = dx * k, oy = dy * k, oz = dz * k;
      for (const t of m) {
        out.push(makeTri(
          [t.a[0] + ox, t.a[1] + oy, t.a[2] + oz],
          [t.b[0] + ox, t.b[1] + oy, t.b[2] + oz],
          [t.c[0] + ox, t.c[1] + oy, t.c[2] + oz]
        ));
      }
    }
    return { mesh: out };
  }
};

const POLAR_ARRAY: NodeTypeDef = {
  type: 'array.polar',
  category: 'Dizi',
  label: 'Polar Dizi',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'count', name: 'Sayı', type: 'number', defaultValue: 6 },
    { id: 'radius', name: 'Yarıçap', type: 'number', defaultValue: 50 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const n = Math.max(1, Math.floor(Number(i.count)));
    const r = Number(i.radius);
    const out: Triangle[] = [];
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      const c = Math.cos(a), s = Math.sin(a);
      const ox = c * r, oy = s * r;
      const fn = (p: Vec3): Vec3 => [c * p[0] - s * p[1] + ox, s * p[0] + c * p[1] + oy, p[2]];
      for (const t of m) {
        out.push(makeTri(fn(t.a), fn(t.b), fn(t.c)));
      }
    }
    return { mesh: out };
  }
};

const GRID_ARRAY: NodeTypeDef = {
  type: 'array.grid',
  category: 'Dizi',
  label: 'Izgara Dizi',
  inputs: [
    { id: 'mesh', name: 'Mesh', type: 'mesh' },
    { id: 'countX', name: 'X sayı', type: 'number', defaultValue: 3 },
    { id: 'countY', name: 'Y sayı', type: 'number', defaultValue: 3 },
    { id: 'dx', name: 'X aralık', type: 'number', defaultValue: 30 },
    { id: 'dy', name: 'Y aralık', type: 'number', defaultValue: 30 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => {
    const m = (i.mesh as Triangle[]) ?? [];
    const nx = Math.max(1, Math.floor(Number(i.countX)));
    const ny = Math.max(1, Math.floor(Number(i.countY)));
    const dx = Number(i.dx);
    const dy = Number(i.dy);
    const out: Triangle[] = [];
    // Center the grid around origin so the user can position it cleanly with
    // a downstream transform.translate.
    const ox0 = -((nx - 1) * dx) / 2;
    const oy0 = -((ny - 1) * dy) / 2;
    for (let yi = 0; yi < ny; yi++) {
      for (let xi = 0; xi < nx; xi++) {
        const ox = ox0 + xi * dx;
        const oy = oy0 + yi * dy;
        for (const t of m) {
          out.push({
            a: [t.a[0] + ox, t.a[1] + oy, t.a[2]],
            b: [t.b[0] + ox, t.b[1] + oy, t.b[2]],
            c: [t.c[0] + ox, t.c[1] + oy, t.c[2]],
            normal: t.normal
          });
        }
      }
    }
    return { mesh: out };
  }
};

// ---- Math (number → number) ----

const MATH_ADD: NodeTypeDef = {
  type: 'math.add',
  category: 'Matematik',
  label: 'Topla',
  inputs: [
    { id: 'a', name: 'A', type: 'number', defaultValue: 0 },
    { id: 'b', name: 'B', type: 'number', defaultValue: 0 }
  ],
  outputs: [{ id: 'out', name: 'A+B', type: 'number' }],
  compute: (i) => ({ out: Number(i.a) + Number(i.b) })
};

const MATH_MULT: NodeTypeDef = {
  type: 'math.multiply',
  category: 'Matematik',
  label: 'Çarp',
  inputs: [
    { id: 'a', name: 'A', type: 'number', defaultValue: 1 },
    { id: 'b', name: 'B', type: 'number', defaultValue: 1 }
  ],
  outputs: [{ id: 'out', name: 'A·B', type: 'number' }],
  compute: (i) => ({ out: Number(i.a) * Number(i.b) })
};

const MATH_SIN: NodeTypeDef = {
  type: 'math.sin',
  category: 'Matematik',
  label: 'Sinüs',
  inputs: [
    { id: 'x', name: 'X (°)', type: 'number', defaultValue: 0 },
    { id: 'amp', name: 'Genlik', type: 'number', defaultValue: 1 }
  ],
  outputs: [{ id: 'out', name: 'sin(x)·A', type: 'number' }],
  compute: (i) => ({ out: Math.sin((Number(i.x) * Math.PI) / 180) * Number(i.amp) })
};

// ---- Cone primitive ----

function buildCone(rBot: number, rTop: number, height: number, segments: number): Triangle[] {
  const seg = Math.max(6, Math.floor(segments));
  const tris: Triangle[] = [];
  const top: Vec3[] = [];
  const bot: Vec3[] = [];
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    bot.push([Math.cos(a) * rBot, Math.sin(a) * rBot, 0]);
    top.push([Math.cos(a) * rTop, Math.sin(a) * rTop, height]);
  }
  for (let i = 0; i < seg; i++) {
    const j = (i + 1) % seg;
    tris.push(...quad(bot[i], bot[j], top[j], top[i]));
  }
  const cb: Vec3 = [0, 0, 0];
  const ct: Vec3 = [0, 0, height];
  for (let i = 0; i < seg; i++) {
    const j = (i + 1) % seg;
    if (rBot > 1e-3) tris.push(makeTri(cb, bot[j], bot[i]));
    if (rTop > 1e-3) tris.push(makeTri(ct, top[i], top[j]));
  }
  return tris;
}

/** Build a torus around the Z axis (lying flat, like a donut on a table).
 *  R = major radius (centerline circle), r = minor radius (tube thickness).
 *  Used for ring decorations on vases (large R, small r) AND for handles
 *  (R = handle arc radius, r = handle stick thickness; usually rotated 90°
 *  so the donut stands upright on the side of a vase). */
function buildTorus(R: number, r: number, segments: number, sides: number): Triangle[] {
  const seg = Math.max(8, Math.floor(segments));
  const sid = Math.max(6, Math.floor(sides));
  const grid: Vec3[][] = [];
  for (let i = 0; i < seg; i++) {
    const u = (i / seg) * Math.PI * 2;
    const cu = Math.cos(u), su = Math.sin(u);
    const ring: Vec3[] = [];
    for (let j = 0; j < sid; j++) {
      const v = (j / sid) * Math.PI * 2;
      const cv = Math.cos(v), sv = Math.sin(v);
      ring.push([(R + r * cv) * cu, (R + r * cv) * su, r * sv]);
    }
    grid.push(ring);
  }
  const tris: Triangle[] = [];
  for (let i = 0; i < seg; i++) {
    const ni = (i + 1) % seg;
    for (let j = 0; j < sid; j++) {
      const nj = (j + 1) % sid;
      tris.push(...quad(grid[i][j], grid[ni][j], grid[ni][nj], grid[i][nj]));
    }
  }
  return tris;
}

const TORUS: NodeTypeDef = {
  type: 'primitive.torus',
  category: 'Şekil',
  label: 'Torus',
  inputs: [
    { id: 'R', name: 'Büyük R', type: 'number', defaultValue: 30 },
    { id: 'r', name: 'Küçük R', type: 'number', defaultValue: 5 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 },
    { id: 'sides', name: 'Kenar', type: 'number', defaultValue: 12 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildTorus(i.R as number, i.r as number, i.segments as number, i.sides as number)
  })
};

/** Cylinder oriented along an arbitrary axis (0=X, 1=Y, 2=Z=default).
 *  Eliminates the rotate-then-translate dance the LLM otherwise needs for
 *  sideways handles, axles, screws, etc. */
function buildTube(r: number, h: number, segments: number, axis: number): Triangle[] {
  const tris = buildCylinder(r, h, segments);   // along +Z
  if (axis === 2) return tris;
  // Rotate via swapping coordinate components in place — cheaper than building
  // a matrix. axis=0 → make the cylinder run along +X; axis=1 → along +Y.
  return tris.map((t) => {
    const swap = (v: Vec3): Vec3 => {
      if (axis === 0) return [v[2], v[1], -v[0]];   // X-aligned
      if (axis === 1) return [v[0], v[2], -v[1]];   // Y-aligned
      return v;
    };
    return makeTri(swap(t.a), swap(t.b), swap(t.c));
  });
}

const TUBE: NodeTypeDef = {
  type: 'primitive.tube',
  category: 'Şekil',
  label: 'Tüp',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 10 },
    { id: 'h', name: 'Uzunluk', type: 'number', defaultValue: 50 },
    { id: 'axis', name: 'Eksen', type: 'number', defaultValue: 2 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 24 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildTube(i.r as number, i.h as number, i.segments as number, i.axis as number)
  })
};

/** Capsule = cylinder with hemispherical caps. Organic shape — limbs,
 *  rounded bars, character body parts, ergonomic handles. */
function buildCapsule(r: number, h: number, segments: number, rings: number): Triangle[] {
  const seg = Math.max(8, Math.floor(segments));
  const rng = Math.max(4, Math.floor(rings));
  const tris: Triangle[] = [];
  // Bottom hemisphere (z = 0 down to -r)
  // Use sphere math but only lower half.
  const grid: Vec3[][] = [];
  // Lower hemisphere from south pole up to equator
  for (let i = 0; i <= rng; i++) {
    const v = (i / rng) * 0.5 * Math.PI;       // 0..π/2
    const ringZ = -Math.cos(v) * r;
    const ringR = Math.sin(v) * r;
    const ring: Vec3[] = [];
    for (let j = 0; j < seg; j++) {
      const u = (j / seg) * Math.PI * 2;
      ring.push([Math.cos(u) * ringR, Math.sin(u) * ringR, ringZ]);
    }
    grid.push(ring);
  }
  // Cylinder middle section: equator down then up.
  // Equator already at grid[rng] (z=0). Add another row at z=h (top equator).
  const topEquator: Vec3[] = grid[rng].map((p) => [p[0], p[1], p[2] + h]);
  grid.push(topEquator);
  // Upper hemisphere from top equator up to north pole.
  for (let i = 1; i <= rng; i++) {
    const v = (i / rng) * 0.5 * Math.PI;
    const ringZ = h + Math.sin(v) * r;
    const ringR = Math.cos(v) * r;
    const ring: Vec3[] = [];
    for (let j = 0; j < seg; j++) {
      const u = (j / seg) * Math.PI * 2;
      ring.push([Math.cos(u) * ringR, Math.sin(u) * ringR, ringZ]);
    }
    grid.push(ring);
  }
  for (let i = 0; i < grid.length - 1; i++) {
    for (let j = 0; j < seg; j++) {
      const j1 = (j + 1) % seg;
      tris.push(...quad(grid[i][j], grid[i + 1][j], grid[i + 1][j1], grid[i][j1]));
    }
  }
  return tris;
}

const CAPSULE: NodeTypeDef = {
  type: 'primitive.capsule',
  category: 'Şekil',
  label: 'Kapsül',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 8 },
    { id: 'h', name: 'Orta uzunluk', type: 'number', defaultValue: 30 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 20 },
    { id: 'rings', name: 'Halka', type: 'number', defaultValue: 8 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildCapsule(i.r as number, i.h as number, i.segments as number, i.rings as number)
  })
};

/** Ellipsoid = sphere scaled along x/y/z. Organic forms (eggs, drops,
 *  flattened gems, character heads). Cheaper than scale-after-sphere because
 *  it doesn't go through transform.scale. */
function buildEllipsoid(rx: number, ry: number, rz: number, segments: number, rings: number): Triangle[] {
  const tris = buildSphere(1, segments, rings);
  return tris.map((t) => {
    const scale = (v: Vec3): Vec3 => [v[0] * rx, v[1] * ry, v[2] * rz];
    return makeTri(scale(t.a), scale(t.b), scale(t.c));
  });
}

const ELLIPSOID: NodeTypeDef = {
  type: 'primitive.ellipsoid',
  category: 'Şekil',
  label: 'Elipsoid',
  inputs: [
    { id: 'rx', name: 'X yarıçap', type: 'number', defaultValue: 20 },
    { id: 'ry', name: 'Y yarıçap', type: 'number', defaultValue: 14 },
    { id: 'rz', name: 'Z yarıçap', type: 'number', defaultValue: 18 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 24 },
    { id: 'rings', name: 'Halka', type: 'number', defaultValue: 16 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildEllipsoid(i.rx as number, i.ry as number, i.rz as number, i.segments as number, i.rings as number)
  })
};

/** N-sided regular prism (hexagonal pencil, octagonal column, triangular wedge). */
function buildPrism(r: number, h: number, sides: number): Triangle[] {
  const n = Math.max(3, Math.floor(sides));
  return buildCylinder(r, h, n);
}

const PRISM: NodeTypeDef = {
  type: 'primitive.prism',
  category: 'Şekil',
  label: 'Prizma',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 15 },
    { id: 'h', name: 'Yükseklik', type: 'number', defaultValue: 40 },
    { id: 'sides', name: 'Kenar sayısı', type: 'number', defaultValue: 6 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildPrism(i.r as number, i.h as number, i.sides as number)
  })
};

/** Flat ring (washer / picture frame) — outer disk minus inner disk, extruded
 *  to thickness h. Built as 4 walls (top, bottom, inner cylinder, outer
 *  cylinder) so we don't need true CSG subtraction. */
function buildRing(rOuter: number, rInner: number, h: number, segments: number): Triangle[] {
  const seg = Math.max(8, Math.floor(segments));
  const ri = Math.min(rInner, rOuter - 0.1);   // guarantee non-degenerate
  const tris: Triangle[] = [];
  const outerBot: Vec3[] = [], outerTop: Vec3[] = [];
  const innerBot: Vec3[] = [], innerTop: Vec3[] = [];
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    outerBot.push([ca * rOuter, sa * rOuter, 0]);
    outerTop.push([ca * rOuter, sa * rOuter, h]);
    innerBot.push([ca * ri,    sa * ri,    0]);
    innerTop.push([ca * ri,    sa * ri,    h]);
  }
  for (let i = 0; i < seg; i++) {
    const j = (i + 1) % seg;
    // Outer wall
    tris.push(...quad(outerBot[i], outerBot[j], outerTop[j], outerTop[i]));
    // Inner wall (reversed winding so normal faces inward)
    tris.push(...quad(innerBot[j], innerBot[i], innerTop[i], innerTop[j]));
    // Top annulus
    tris.push(...quad(innerTop[i], innerTop[j], outerTop[j], outerTop[i]));
    // Bottom annulus (reversed)
    tris.push(...quad(innerBot[j], innerBot[i], outerBot[i], outerBot[j]));
  }
  return tris;
}

const RING: NodeTypeDef = {
  type: 'primitive.ring',
  category: 'Şekil',
  label: 'Halka',
  inputs: [
    { id: 'rOuter', name: 'Dış R', type: 'number', defaultValue: 30 },
    { id: 'rInner', name: 'İç R', type: 'number', defaultValue: 22 },
    { id: 'h', name: 'Kalınlık', type: 'number', defaultValue: 4 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildRing(i.rOuter as number, i.rInner as number, i.h as number, i.segments as number)
  })
};

const CONE: NodeTypeDef = {
  type: 'primitive.cone',
  category: 'Şekil',
  label: 'Koni',
  inputs: [
    { id: 'rBot', name: 'Alt R', type: 'number', defaultValue: 20 },
    { id: 'rTop', name: 'Üst R', type: 'number', defaultValue: 0 },
    { id: 'h', name: 'Yükseklik', type: 'number', defaultValue: 40 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildCone(Number(i.rBot), Number(i.rTop), Number(i.h), Number(i.segments)) })
};

const OUTPUT: NodeTypeDef = {
  type: 'output.mesh',
  category: 'Çıktı',
  label: 'Çıktı',
  inputs: [{ id: 'in', name: 'Mesh', type: 'mesh' }],
  outputs: [],
  compute: (i) => ({ result: i.in })
};

// ---- CSG (boolean) — true solid modeling ----

const CSG_UNION: NodeTypeDef = {
  type: 'csg.union',
  category: 'Boole',
  label: 'Birleşim (∪)',
  inputs: [
    { id: 'a', name: 'A', type: 'mesh' },
    { id: 'b', name: 'B', type: 'mesh' }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: csgUnion((i.a as Triangle[]) ?? [], (i.b as Triangle[]) ?? []) })
};

const CSG_SUBTRACT: NodeTypeDef = {
  type: 'csg.subtract',
  category: 'Boole',
  label: 'Fark (A−B)',
  inputs: [
    { id: 'a', name: 'A (gövde)', type: 'mesh' },
    { id: 'b', name: 'B (delik)', type: 'mesh' }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: csgSubtract((i.a as Triangle[]) ?? [], (i.b as Triangle[]) ?? []) })
};

const CSG_INTERSECT: NodeTypeDef = {
  type: 'csg.intersect',
  category: 'Boole',
  label: 'Kesişim (∩)',
  inputs: [
    { id: 'a', name: 'A', type: 'mesh' },
    { id: 'b', name: 'B', type: 'mesh' }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: csgIntersect((i.a as Triangle[]) ?? [], (i.b as Triangle[]) ?? []) })
};

// ---- Profiles (closed 2D polylines used by loft/revolve/sweep) ----
// Represented as Vec3[] in the XY plane (z=0). Loft connects two profiles
// at different Z; revolve spins one profile around Z.

function buildCircleProfile(r: number, segments: number): Vec3[] {
  const seg = Math.max(6, Math.floor(segments));
  const pts: Vec3[] = [];
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    pts.push([Math.cos(a) * r, Math.sin(a) * r, 0]);
  }
  return pts;
}

function buildRectProfile(sx: number, sy: number): Vec3[] {
  const x = sx / 2, y = sy / 2;
  return [[-x, -y, 0], [x, -y, 0], [x, y, 0], [-x, y, 0]];
}

function buildPolygonProfile(r: number, sides: number): Vec3[] {
  const n = Math.max(3, Math.floor(sides));
  const pts: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    pts.push([Math.cos(a) * r, Math.sin(a) * r, 0]);
  }
  return pts;
}

const PROFILE_CIRCLE: NodeTypeDef = {
  type: 'profile.circle',
  category: 'Profil',
  label: 'Daire profili',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 20 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 }
  ],
  outputs: [{ id: 'profile', name: 'Profil', type: 'profile' }],
  compute: (i) => ({ profile: buildCircleProfile(i.r as number, i.segments as number) })
};

const PROFILE_RECT: NodeTypeDef = {
  type: 'profile.rect',
  category: 'Profil',
  label: 'Dikdörtgen profili',
  inputs: [
    { id: 'sx', name: 'Genişlik', type: 'number', defaultValue: 30 },
    { id: 'sy', name: 'Derinlik', type: 'number', defaultValue: 30 }
  ],
  outputs: [{ id: 'profile', name: 'Profil', type: 'profile' }],
  compute: (i) => ({ profile: buildRectProfile(i.sx as number, i.sy as number) })
};

const PROFILE_POLYGON: NodeTypeDef = {
  type: 'profile.polygon',
  category: 'Profil',
  label: 'Çokgen profili',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 20 },
    { id: 'sides', name: 'Kenar', type: 'number', defaultValue: 6 }
  ],
  outputs: [{ id: 'profile', name: 'Profil', type: 'profile' }],
  compute: (i) => ({ profile: buildPolygonProfile(i.r as number, i.sides as number) })
};

// ---- Loft / Revolve ----

/** Loft connects two profiles at z=z0 and z=z1 with a quad ring. Profiles
 *  must have the SAME number of points; we resample by index pairing. Top
 *  and bottom can be capped (triangle fan to centroid). */
function buildLoft(profileA: Vec3[], profileB: Vec3[], z0: number, z1: number, capBottom: boolean, capTop: boolean): Triangle[] {
  if (!profileA || !profileB || profileA.length === 0 || profileB.length === 0) return [];
  // Resample to the larger count by linear index interpolation. Cheap and
  // gives reasonable correspondence for similar shapes.
  const n = Math.max(profileA.length, profileB.length);
  const sample = (p: Vec3[], i: number, z: number): Vec3 => {
    const t = (i / n) * p.length;
    const i0 = Math.floor(t) % p.length;
    const i1 = (i0 + 1) % p.length;
    const f = t - Math.floor(t);
    const a = p[i0], b = p[i1];
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, z];
  };
  const ringA: Vec3[] = [], ringB: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    ringA.push(sample(profileA, i, z0));
    ringB.push(sample(profileB, i, z1));
  }
  const tris: Triangle[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    tris.push(...quad(ringA[i], ringA[j], ringB[j], ringB[i]));
  }
  if (capBottom) {
    const cx = ringA.reduce((s, p) => s + p[0], 0) / n;
    const cy = ringA.reduce((s, p) => s + p[1], 0) / n;
    const c: Vec3 = [cx, cy, z0];
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      tris.push(makeTri(c, ringA[j], ringA[i]));
    }
  }
  if (capTop) {
    const cx = ringB.reduce((s, p) => s + p[0], 0) / n;
    const cy = ringB.reduce((s, p) => s + p[1], 0) / n;
    const c: Vec3 = [cx, cy, z1];
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      tris.push(makeTri(c, ringB[i], ringB[j]));
    }
  }
  return tris;
}

const LOFT: NodeTypeDef = {
  type: 'surface.loft',
  category: 'Yüzey',
  label: 'Loft',
  inputs: [
    { id: 'a', name: 'Alt profil', type: 'profile' },
    { id: 'b', name: 'Üst profil', type: 'profile' },
    { id: 'z0', name: 'Alt z', type: 'number', defaultValue: 0 },
    { id: 'z1', name: 'Üst z', type: 'number', defaultValue: 50 },
    { id: 'capBot', name: 'Alt kapak', type: 'number', defaultValue: 1 },
    { id: 'capTop', name: 'Üst kapak', type: 'number', defaultValue: 1 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({
    mesh: buildLoft(
      i.a as Vec3[], i.b as Vec3[],
      i.z0 as number, i.z1 as number,
      Number(i.capBot) > 0.5, Number(i.capTop) > 0.5
    )
  })
};

/** Revolve a 2D profile (XZ plane: vertices have x = radius, y unused, z = height)
 *  around the Z axis. Closed surface unless the profile starts/ends on the axis. */
function buildRevolve(profile: Vec3[], segments: number): Triangle[] {
  if (!profile || profile.length < 2) return [];
  const seg = Math.max(8, Math.floor(segments));
  // Profile interpreted as (radius, _, height) — we use x and z, ignore y.
  const tris: Triangle[] = [];
  const grid: Vec3[][] = [];
  for (let s = 0; s < seg; s++) {
    const a = (s / seg) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    const ring: Vec3[] = [];
    for (const p of profile) {
      const r = p[0];
      const z = p[2];
      ring.push([ca * r, sa * r, z]);
    }
    grid.push(ring);
  }
  for (let s = 0; s < seg; s++) {
    const ns = (s + 1) % seg;
    for (let i = 0; i < profile.length - 1; i++) {
      tris.push(...quad(grid[s][i], grid[ns][i], grid[ns][i + 1], grid[s][i + 1]));
    }
  }
  return tris;
}

const REVOLVE: NodeTypeDef = {
  type: 'surface.revolve',
  category: 'Yüzey',
  label: 'Devir (Revolve)',
  inputs: [
    { id: 'profile', name: 'Profil', type: 'profile' },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 32 }
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildRevolve(i.profile as Vec3[], i.segments as number) })
};

/** Sweep a 2D profile along a path. The profile is centered at each path
 *  point with its plane perpendicular to the path tangent. Uses a parallel-
 *  transport frame (no twist) — adequate for handles, pipes, organic tubes. */
function buildSweep(profile: Vec3[], path: Vec3[]): Triangle[] {
  if (!profile || profile.length < 3 || !path || path.length < 2) return [];
  const tris: Triangle[] = [];
  const rings: Vec3[][] = [];
  // Initial frame: pick any axis not parallel to first tangent.
  let prevTangent: Vec3 = norm(sub(path[1], path[0]));
  let up: Vec3 = Math.abs(prevTangent[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  let side: Vec3 = norm(cross(up, prevTangent));
  up = cross(prevTangent, side);
  for (let pi = 0; pi < path.length; pi++) {
    const center = path[pi];
    const next = pi < path.length - 1 ? path[pi + 1] : path[pi];
    const prev = pi > 0 ? path[pi - 1] : path[pi];
    const tangent = norm(sub(next, prev));
    // Parallel transport: rotate the up vector minimally to be perpendicular
    // to the new tangent.
    if (pi > 0) {
      side = norm(cross(up, tangent));
      up = cross(tangent, side);
    }
    const ring: Vec3[] = [];
    for (const p of profile) {
      const x = p[0], y = p[1];
      ring.push([
        center[0] + side[0] * x + up[0] * y,
        center[1] + side[1] * x + up[1] * y,
        center[2] + side[2] * x + up[2] * y
      ]);
    }
    rings.push(ring);
    prevTangent = tangent;
  }
  const n = profile.length;
  for (let pi = 0; pi < rings.length - 1; pi++) {
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      tris.push(...quad(rings[pi][i], rings[pi][j], rings[pi + 1][j], rings[pi + 1][i]));
    }
  }
  return tris;
}

const SWEEP: NodeTypeDef = {
  type: 'surface.sweep',
  category: 'Yüzey',
  label: 'Süpürme (Sweep)',
  inputs: [
    { id: 'profile', name: 'Profil', type: 'profile' },
    { id: 'path', name: 'Yol', type: 'profile' }    // path is also a Vec3[] — reuse profile slot
  ],
  outputs: [{ id: 'mesh', name: 'Mesh', type: 'mesh' }],
  compute: (i) => ({ mesh: buildSweep(i.profile as Vec3[], i.path as Vec3[]) })
};

/** Path generators (output 'profile' type so they can feed sweep). */
const PATH_LINE: NodeTypeDef = {
  type: 'path.line',
  category: 'Profil',
  label: 'Doğru yol',
  inputs: [
    { id: 'x0', name: 'A.x', type: 'number', defaultValue: 0 },
    { id: 'y0', name: 'A.y', type: 'number', defaultValue: 0 },
    { id: 'z0', name: 'A.z', type: 'number', defaultValue: 0 },
    { id: 'x1', name: 'B.x', type: 'number', defaultValue: 0 },
    { id: 'y1', name: 'B.y', type: 'number', defaultValue: 0 },
    { id: 'z1', name: 'B.z', type: 'number', defaultValue: 50 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 8 }
  ],
  outputs: [{ id: 'path', name: 'Yol', type: 'profile' }],
  compute: (i) => {
    const n = Math.max(2, Math.floor(Number(i.segments)));
    const pts: Vec3[] = [];
    for (let k = 0; k < n; k++) {
      const t = k / (n - 1);
      pts.push([
        (i.x0 as number) + ((i.x1 as number) - (i.x0 as number)) * t,
        (i.y0 as number) + ((i.y1 as number) - (i.y0 as number)) * t,
        (i.z0 as number) + ((i.z1 as number) - (i.z0 as number)) * t
      ]);
    }
    return { path: pts };
  }
};

const PATH_ARC: NodeTypeDef = {
  type: 'path.arc',
  category: 'Profil',
  label: 'Yay yol',
  inputs: [
    { id: 'r', name: 'Yarıçap', type: 'number', defaultValue: 30 },
    { id: 'startDeg', name: 'Baş°', type: 'number', defaultValue: 0 },
    { id: 'endDeg', name: 'Son°', type: 'number', defaultValue: 180 },
    { id: 'cz', name: 'Merkez z', type: 'number', defaultValue: 0 },
    { id: 'segments', name: 'Segment', type: 'number', defaultValue: 16 }
  ],
  outputs: [{ id: 'path', name: 'Yol', type: 'profile' }],
  compute: (i) => {
    const n = Math.max(2, Math.floor(Number(i.segments)));
    const r = i.r as number;
    const a0 = ((i.startDeg as number) * Math.PI) / 180;
    const a1 = ((i.endDeg as number) * Math.PI) / 180;
    const cz = i.cz as number;
    const pts: Vec3[] = [];
    for (let k = 0; k < n; k++) {
      const t = k / (n - 1);
      const a = a0 + (a1 - a0) * t;
      pts.push([Math.cos(a) * r, Math.sin(a) * r, cz]);
    }
    return { path: pts };
  }
};

export const NODE_REGISTRY: Map<string, NodeTypeDef> = new Map(
  [NUMBER, VECTOR, MATH_ADD, MATH_MULT, MATH_SIN,
   BOX, CYLINDER, TUBE, CONE, SPHERE, ELLIPSOID, CAPSULE, PRISM, TORUS, RING, VASE_NODE,
   PROFILE_CIRCLE, PROFILE_RECT, PROFILE_POLYGON, PATH_LINE, PATH_ARC,
   LOFT, REVOLVE, SWEEP,
   CSG_UNION, CSG_SUBTRACT, CSG_INTERSECT,
   TRANSLATE, ROTATE, SCALE, MERGE, ARRAY, GRID_ARRAY, POLAR_ARRAY, OUTPUT].map((d) => [d.type, d])
);

/** Group node defs by category for the add-menu. */
export function nodesByCategory(): Map<string, NodeTypeDef[]> {
  const map = new Map<string, NodeTypeDef[]>();
  for (const def of NODE_REGISTRY.values()) {
    if (!map.has(def.category)) map.set(def.category, []);
    map.get(def.category)!.push(def);
  }
  return map;
}
